/**
 * Marketing team member's task board.
 *
 * Replaces `renderTeamDashboardMyTasks` and `toggleCurrentTeamMemberCapacity`
 * (team_dashboard.js), plus the never-loaded `team_member_dashboard.js` — a
 * 266 KB, 4,983-line file that `index.html` does not reference at all and that
 * therefore has never run in production.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { teamApi, topicApi } from '../../api/endpoints';
import { isoToDisplayDate, todayKey } from '../../domain/dates';

export function TeamTasks() {
  const { teamMemberId, session } = useAuth();
  const { teamMembers, loadTeamMembers } = useData();
  const { success, error: toastError } = useNotifications();

  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const me = useMemo(
    () => teamMembers.find((m) => String(m.id) === String(teamMemberId)) || null,
    [teamMembers, teamMemberId],
  );

  const projects = useMemo(() => me?.assignedProjects || [], [me]);

  /**
   * Topics across every assigned project.
   *
   * There is no "topics for a team member" endpoint, so this fans out over the
   * member's projects — the same thing the legacy dashboard did, but with the
   * results actually awaited rather than raced.
   */
  const topicsState = useAsync(
    async () => {
      if (projects.length === 0) return [];
      const results = await Promise.allSettled(
        projects.map((project) => topicApi.listForPackage(project.id)),
      );
      return results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result, index) =>
          (result.value || []).map((topic) => ({
            ...topic,
            projectId: projects[index]?.id,
            doctorName: projects[index]?.doctorName,
          })),
        );
    },
    [projects.map((p) => p.id).join(',')],
    { initialData: [] },
  );

  const topics = useMemo(() => topicsState.data || [], [topicsState.data]);

  const mine = useMemo(
    () => topics.filter((topic) => String(topic.assigned_team_member_id) === String(teamMemberId)),
    [topics, teamMemberId],
  );

  const visible = useMemo(() => {
    if (filter === 'mine') return mine;
    if (filter === 'pending') return topics.filter((t) => !t.is_done);
    if (filter === 'done') return topics.filter((t) => t.is_done);
    return topics;
  }, [topics, mine, filter]);

  const totals = useMemo(
    () => ({
      projects: projects.length,
      topics: topics.length,
      mine: mine.length,
      done: topics.filter((t) => t.is_done).length,
    }),
    [projects, topics, mine],
  );

  const toggleCapacity = async () => {
    if (!me) return;
    setBusy(true);
    try {
      await teamApi.updateMember(me.id, {
        capacityStatus: me.capacityStatus === 'full' ? 'available' : 'full',
      });
      success(me.capacityStatus === 'full' ? 'تم تعليمك كمتاح' : 'تم تعليمك كمشغول');
      loadTeamMembers();
    } catch (err) {
      toastError(err.message || 'تعذر تغيير الحالة');
    } finally {
      setBusy(false);
    }
  };

  const markDone = async (topic, isDone) => {
    setBusy(true);
    try {
      await topicApi.setStatus(topic.id, { is_done: isDone });
      success(isDone ? 'تم تعليم المهمة كمنجزة' : 'تم إعادة فتح المهمة');
      topicsState.reload();
    } catch (err) {
      toastError(err.message || 'تعذر تحديث المهمة');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`مهامي — ${session?.name || ''}`}
        subtitle={me?.roles?.length ? me.roles.join('، ') : 'فريق التسويق'}
        icon="list-checks"
        actions={
          <Button
            variant={me?.capacityStatus === 'full' ? 'success' : 'ghost'}
            icon={me?.capacityStatus === 'full' ? 'play' : 'pause'}
            loading={busy}
            onClick={toggleCapacity}
          >
            {me?.capacityStatus === 'full' ? 'أنا متاح الآن' : 'تعليم كمشغول'}
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="briefcase" label="مشاريع مسندة" value={totals.projects} accent="cyan" />
        <StatCard icon="list" label="إجمالي المواضيع" value={totals.topics} accent="purple" />
        <StatCard icon="user-check" label="مواضيعي" value={totals.mine} accent="amber" />
        <StatCard icon="check-circle-2" label="منجزة" value={totals.done} accent="emerald" />
      </div>

      {/* Projects */}
      <Panel>
        <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="briefcase" size={18} className="text-cyan-400" />
          المشاريع المسندة
        </h3>

        {projects.length === 0 ? (
          <EmptyState
            icon="briefcase"
            title="لا توجد مشاريع"
            message="سيظهر هنا كل مشروع تسنده لك الإدارة."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"
              >
                <span className="rounded-lg bg-purple-500/10 p-2.5 text-purple-400">
                  <Icon name="package" size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{project.doctorName}</p>
                  <p className="text-[11px] text-slate-400">مشروع #{project.id}</p>
                </div>
                <Badge className="border-slate-600 bg-slate-800 text-slate-300">
                  {project.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Topics */}
      <Panel>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="list-checks" size={18} className="text-emerald-400" />
            مواضيع المحتوى
          </h3>

          <Select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className="!w-auto !py-2 text-xs"
          >
            <option value="all">كل المواضيع</option>
            <option value="mine">مسندة لي</option>
            <option value="pending">غير منجزة</option>
            <option value="done">منجزة</option>
          </Select>
        </div>

        {topicsState.error ? (
          <ErrorState error={topicsState.error} onRetry={topicsState.reload} />
        ) : topicsState.loading && topics.length === 0 ? (
          <Spinner variant="inline" label="جاري تحميل المهام..." />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="list-x"
            title="لا توجد مهام"
            message={
              filter === 'mine'
                ? 'لم يتم إسناد أي موضوع لك بعد.'
                : 'ستظهر هنا مواضيع المحتوى الخاصة بمشاريعك.'
            }
          />
        ) : (
          <ul className="space-y-2">
            {visible.map((topic) => {
              const isMine = String(topic.assigned_team_member_id) === String(teamMemberId);
              const postDate = topic.post_date ? String(topic.post_date).slice(0, 10) : null;
              const overdue = postDate && !topic.is_done && postDate < todayKey();

              return (
                <li
                  key={topic.id}
                  className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 ${
                    topic.is_done
                      ? 'border-white/5 bg-white/[0.02] opacity-70'
                      : overdue
                        ? 'border-red-500/30 bg-red-500/5'
                        : 'border-white/5 bg-white/5'
                  }`}
                >
                  <span
                    className={`rounded-lg p-2.5 ${
                      topic.is_done
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    <Icon name={topic.is_done ? 'check-circle-2' : 'circle'} size={16} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-sm font-bold ${
                        topic.is_done ? 'text-slate-400 line-through' : 'text-white'
                      }`}
                    >
                      {topic.topic_title || 'بدون عنوان'}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
                      {topic.doctorName && <span>{topic.doctorName}</span>}
                      {postDate && <span>{isoToDisplayDate(postDate)}</span>}
                      {isMine && <span className="text-cyan-400">مسند لي</span>}
                      {overdue && <span className="font-bold text-red-400">متأخر</span>}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {topic.is_approved ? (
                      <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                        معتمد
                      </Badge>
                    ) : (
                      <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                        بانتظار الاعتماد
                      </Badge>
                    )}

                    <Button
                      variant={topic.is_done ? 'subtle' : 'success'}
                      icon={topic.is_done ? 'rotate-ccw' : 'check'}
                      loading={busy}
                      onClick={() => markDone(topic, !topic.is_done)}
                    >
                      {topic.is_done ? 'إعادة فتح' : 'إنجاز'}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}

export default TeamTasks;
